import { Dispatch, MouseEventHandler, SetStateAction, useMemo, useState } from 'react'
import { toRawType } from 'maeng-utils/utils'
import { IonicClose } from 'maeng-icons/ionic'
import type { DialogBehavior, DialogProps, MuiDialogBehavior } from './Dialog.types'
import * as S from './Dialog.styles'
import DialogActions from '../DialogActions'
import Button from '../Button'
import DialogTitle from '../DialogTitle'
import Typography from '../Typography'
import DialogContent from '../DialogContent'
import useDialogAction from '../../hooks/useDialogAction'

type ActionsProps = {
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
} & Pick<DialogProps, 'onConfirm' | 'onCancel' | 'onClose' | 'sx' | 'confirmProps' | 'cancelProps'>

const Actions = ({
  onConfirm,
  onCancel,
  onClose,
  confirmProps = {
    variant: 'contained',
    color: 'primary',
    children: '확인',
    size: 'medium',
  },
  cancelProps = {
    variant: 'outlined',
    children: '취소',
    size: 'medium',
  },
  sx,
  loading,
  setLoading,
}: ActionsProps) => {
  const { confirmProps: computedConfirmProps, cancelProps: computedCancelProps } = useDialogAction({
    confirmProps: {
      ...confirmProps,
      disabled: loading,
      loading,
    },
    cancelProps: {
      ...cancelProps,
      disabled: loading,
    },
  })
  const handleAfter = (e: any, result: any, behavior: DialogBehavior) => {
    switch (toRawType(result)) {
      case 'Promise':
        setLoading(true)
        ;(result as Promise<any>)
          .then((success) => success && onClose?.(e!, behavior))
          .finally(() => setLoading(false))
        break
      case 'Undefined':
        onClose?.(e!, behavior)
        break
      default:
        if (result) onClose?.(e!, behavior)
    }
  }

  const handleConfirm: MouseEventHandler<HTMLButtonElement> = (e) => {
    handleAfter(e, onConfirm!(), 'confirmClick')
  }

  const handleCancel: MouseEventHandler<HTMLButtonElement> = (e) => {
    handleAfter(e, onCancel!(), 'cancelClick')
  }

  return (
    <DialogActions sx={sx}>
      {onCancel && <Button onClick={handleCancel} {...computedCancelProps} />}
      {onConfirm && <Button onClick={handleConfirm} {...computedConfirmProps} />}
    </DialogActions>
  )
}

const Title = ({
  onClose,
  title,
  showCloseButton,
}: Pick<DialogProps, 'onClose' | 'title' | 'showCloseButton'>) => {
  const handleClickCloseButton = (e: any) => {
    onClose?.(e, 'closeClick')
  }
  return (
    <DialogTitle>
      <Typography variant="title1">{title}</Typography>
      {showCloseButton && (
        <S.CloseButton onClick={handleClickCloseButton}>
          <IonicClose size={28} />
        </S.CloseButton>
      )}
    </DialogTitle>
  )
}

const Dialog = ({ onClose, ...props }: DialogProps) => {
  const [loading, setLoading] = useState(false)
  const handleClose = (e: any, b: DialogBehavior) => {
    if (!loading) onClose?.(e, b as MuiDialogBehavior)
  }
  const { children, showCloseButton: _showCloseButton, title, onConfirm, onCancel, ...rest } = props

  const contentPadding = useMemo(() => {
    const top = title ? 0 : 24
    const bottom = onConfirm || onCancel ? 0 : 24

    return `${top}px 24px ${bottom}px`
  }, [onCancel, onConfirm, title])

  return (
    <S.Dialog {...rest}>
      {title && <Title {...props} onClose={handleClose} />}
      {children && <DialogContent sx={{ padding: contentPadding }}>{children}</DialogContent>}
      {(onConfirm || onCancel) && (
        <Actions
          sx={{ padding: title ? '8px 24px 24px;' : '24px' }}
          onClose={handleClose}
          loading={loading}
          setLoading={setLoading}
          {...props}
        />
      )}
    </S.Dialog>
  )
}

export default Dialog
